import { action, makeObservable, observable } from 'mobx';

import { logToNewRelic } from '../../services/debug';
import { resolveFlag } from '../../services/launchDarkly';
import { getErrorMessage } from '../../utils/errors';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import type { MinigameGameType, IframeData, LoaderType } from './types';
import { addPlayerToFrb, postLaunchGame } from './utils';

export class MinigamesStore extends Loadable {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    this.initIframePostMessage();
    makeObservable(this);
  }

  @observable
  public loaderType: LoaderType = 'default';
  @action
  public setLoaderType(loaderType: LoaderType) {
    this.loaderType = loaderType;
  }

  private initIframePostMessage() {
    window.addEventListener('message', (e) => {
      const message = e.data;
      const gameId = message?.payload?.gameId;
      if (message?.type === 'open_minigame' && gameId) {
        void this.startMinigame(
          gameId,
          () => {
            // Notify the iframe that the minigame has been closed
            console.log('Posting minigame_closed message to iframe', e);
            e?.source?.postMessage({ type: 'minigame_closed' }, { targetOrigin: e.origin });
          },
          'curtain',
        );
      }
    });
  }

  @observable
  public currentGameType: MinigameGameType | null = null;

  @action
  public setCurrentGameType(gameType: MinigameGameType | null) {
    this.currentGameType = gameType;
  }

  @observable
  public isDialogOpen = false;

  @action
  public setDialogOpen(isOpen: boolean) {
    this.isDialogOpen = isOpen;
  }

  @observable
  public loadingMinigame = false;

  @action
  public setLoadingMinigame(loading: boolean) {
    this.loadingMinigame = loading;
  }

  @observable
  public iframeData: IframeData | null = null;

  @action
  private setIframeData(data: IframeData | null) {
    this.iframeData = data;
  }

  private onCloseListener: VoidFunction | null = null;
  setOnCloseListener(listener: VoidFunction | null) {
    this.onCloseListener = listener;
  }

  public async startMinigame(gameId: string | number, onClose?: VoidFunction, loaderType: LoaderType = 'default') {
    const { userFreeSpinsStore } = this.rootStore;

    this.setLoaderType(loaderType);
    this.setLoadingMinigame(true);

    if (onClose) {
      this.setOnCloseListener(onClose);
    }

    this.setDialogOpen(true);

    // If it's from widget click
    const isFromFreeSpinWidget = Boolean(userFreeSpinsStore.selectedFreeSpin);
    if (!isFromFreeSpinWidget) {
      void userFreeSpinsStore.fetch();
    }
    if (userFreeSpinsStore.selectedFreeSpin?.bonusCode) {
      const featureFlag = resolveFlag('fs_widget_add_player_frb', true);
      if (featureFlag) {
        await addPlayerToFrb(userFreeSpinsStore.selectedFreeSpin.bonusCode);
      }
    }
    const gameData = await this.launchGame(gameId);

    if (gameData) {
      const gameUrl = gameData.slotGameUrl || gameData.gameUrl;
      const url = new URL(gameUrl);
      url.searchParams.set('disabledSounds', String(this.rootStore.uiStore.disabledSounds));
      this.setIframeData({ src: url.toString() });
    } else {
      this.closeMinigame();
    }
    this.setLoadingMinigame(false);
  }

  public closeMinigame(withError = false) {
    this.setIframeData(null);
    this.setDialogOpen(false);
    this.onCloseListener?.();
    this.setOnCloseListener(null);
    this.setError('');

    if (!withError) {
      void this.rootStore.userFreeSpinsStore.fetch();
    }
  }

  public async launchGame(gameId: string | number) {
    const { coinType } = this.rootStore.coinTypeStore;

    if (this.loading) return;
    this.setLoading(true);

    try {
      const launchGameData = await postLaunchGame({ gameId, coin: coinType });
      this.setLoading(false);
      return launchGameData;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      void logToNewRelic(
        'Error launching game FE',
        'Error launching game FE: ' + JSON.stringify({ gameId, coinType, errorMessage }),
        'error',
      );
    }

    this.setLoading(false);
  }
}
